import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useViewerPresence } from '@/hooks/useViewerPresence';
import { BackButton } from '@/components/BackButton';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Coins, Heart, Send, Users, Share2, Trophy, Scissors, PictureInPicture2 } from 'lucide-react';
import { toast } from 'sonner';
import { chatMessageSchema, tipSchema } from '@/lib/validationSchemas';
import { TipCelebration } from '@/components/stream/TipCelebration';
import { LivePlayer } from '@/components/stream/LivePlayer';
import { WebRTCPlayer } from '@/components/stream/WebRTCPlayer';
import { LiveReactions } from '@/components/stream/LiveReactions';
import { ClipCreator } from '@/components/stream/ClipCreator';

const StreamPlayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const { viewerCount } = useViewerPresence(id || '', currentUserId || undefined);
  const [stream, setStream] = useState<any>(null);
  const [streamManagement, setStreamManagement] = useState<any>(null);
  const [polls, setPolls] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [tipAmount, setTipAmount] = useState('');
  const [wallet, setWallet] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [streamNotFound, setStreamNotFound] = useState(false);
  const [tipCelebration, setTipCelebration] = useState<{ amount: number; userName: string } | null>(null);
  const [currentUserName, setCurrentUserName] = useState<string>('');
  const [showClipCreator, setShowClipCreator] = useState(false);
  const [isPiPActive, setIsPiPActive] = useState(false);

  useEffect(() => {
    if (!id) return;
    
    const initUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
      
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', user.id)
          .maybeSingle();
        setCurrentUserName(profile?.display_name || 'User');
      }
    };
    
    initUser();
    loadStream();
    loadPolls();
    loadWallet();
    subscribeToChat();
    subscribeToTips();
    subscribeToStreamStatus();
  }, [id]);

  const loadStream = async () => {
    try {
      const { data, error } = await supabase
        .from('streams')
        .select('*, profiles(display_name, avatar_url)')
        .eq('id', id)
        .single();
      
      if (error || !data) {
        setStreamNotFound(true);
        setLoading(false);
        toast.error('Stream not found');
        setTimeout(() => navigate('/discovery'), 2000);
        return;
      }

      setStream(data);
      
      const { data: mgmtData } = await supabase
        .from('streams_management')
        .select('*')
        .eq('id', id)
        .single();
      
      if (mgmtData) {
        setStreamManagement(mgmtData);
      }
      
      setLoading(false);
    } catch (error) {
      setStreamNotFound(true);
      setLoading(false);
    }
  };

  const loadPolls = async () => {
    const { data } = await supabase
      .from('polls')
      .select('*')
      .eq('stream_id', id);
    
    if (data) {
      setPolls(data);
    }
  };

  const loadWallet = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data } = await supabase
      .from('wallets')
      .select('*')
      .eq('user_id', user.id)
      .single();
    
    if (data) setWallet(data);
  };

  const subscribeToTips = () => {
    const channel = supabase
      .channel(`tips-${id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'tips',
        filter: `stream_id=eq.${id}`
      }, async (payload) => {
        const { data: senderProfile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', payload.new.from_user_id)
          .maybeSingle();

        const tipMessage = {
          id: `tip-${payload.new.id}`,
          user_id: payload.new.from_user_id,
          message: `sent ${payload.new.amount} coins! 💰`,
          created_at: payload.new.created_at,
          is_tip: true,
          sender_name: senderProfile?.display_name || 'Anonymous'
        };
        
        setMessages(prev => [...prev, tipMessage]);
        setTipCelebration({ amount: payload.new.amount, userName: senderProfile?.display_name || 'Anonymous' });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const subscribeToStreamStatus = () => {
    const channel = supabase
      .channel(`stream-status-${id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'streams',
        filter: `id=eq.${id}`
      }, (payload) => {
        if (payload.new.status === 'ended') {
          toast.error('This stream has ended');
          setTimeout(() => navigate('/discovery'), 3000);
        } else {
          setStream(payload.new);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const subscribeToChat = async () => {
    const { data: existingMessages } = await supabase
      .from('chat_messages')
      .select('*')
      .eq('stream_id', id)
      .order('created_at', { ascending: false })
      .limit(50);

    if (existingMessages) {
      const reversedMessages = existingMessages.reverse();
      const userIds = [...new Set(reversedMessages.map(msg => msg.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p.display_name]) || []);

      setMessages(reversedMessages.map(msg => ({
        ...msg,
        sender_name: profileMap.get(msg.user_id) || 'User'
      })));
    }

    const channel = supabase
      .channel(`stream-${id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `stream_id=eq.${id}`
      }, async (payload) => {
        const { data: senderProfile } = await supabase
          .from('profiles')
          .select('display_name')
          .eq('id', payload.new.user_id)
          .maybeSingle();

        setMessages(prev => [...prev, {
          ...payload.new,
          sender_name: senderProfile?.display_name || 'User'
        }]);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const sendMessage = async () => {
    const validation = chatMessageSchema.safeParse({ message: newMessage });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }
    
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Please login to chat');
      return;
    }

    const { error } = await supabase
      .from('chat_messages')
      .insert({
        stream_id: id,
        user_id: user.id,
        message: validation.data.message
      });

    if (error) {
      toast.error('Failed to send message');
    } else {
      setNewMessage('');
    }
  };

  const sendTip = async () => {
    const amount = parseInt(tipAmount);
    
    const validation = tipSchema.safeParse({ amount });
    if (!validation.success) {
      toast.error(validation.error.errors[0].message);
      return;
    }

    if (!wallet || wallet.balance < validation.data.amount) {
      const needed = validation.data.amount - (wallet?.balance || 0);
      toast.error(`Insufficient balance. You need ${needed} more coins`, {
        action: {
          label: 'Top Up',
          onClick: () => navigate('/wallet')
        }
      });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('tips')
      .insert({
        stream_id: id,
        from_user_id: user.id,
        to_user_id: stream.creator_id,
        amount: validation.data.amount
      });

    if (error) {
      toast.error('Failed to send tip');
    } else {
      toast.success(`✨ Tip sent! ${validation.data.amount} coins`);
      setTipAmount('');
      loadWallet();
    }
  };

  const voteOnPoll = async (pollId: string, optionIndex: number, cost: number) => {
    if (!wallet || wallet.balance < cost) {
      toast.error('Insufficient balance');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('votes')
      .insert({
        poll_id: pollId,
        user_id: user.id,
        option_index: optionIndex
      });

    if (error) {
      toast.error('Failed to vote');
    } else {
      toast.success('Vote submitted!');
      loadWallet();
      loadPolls();
    }
  };

  const togglePictureInPicture = async () => {
    try {
      const video = document.querySelector('video');
      if (!video) {
        toast.error('Video element not found');
        return;
      }

      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiPActive(false);
      } else if (document.pictureInPictureEnabled) {
        await video.requestPictureInPicture();
        setIsPiPActive(true);
        
        video.addEventListener('leavepictureinpicture', () => {
          setIsPiPActive(false);
        }, { once: true });
      }
    } catch (error) {
      console.error('PiP error:', error);
      toast.error('Picture-in-picture not supported');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-white">Loading stream...</p>
        </div>
      </div>
    );
  }

  if (streamNotFound || !stream) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <Users className="h-16 w-16 mx-auto mb-4 text-white/20" />
          <h2 className="text-2xl font-bold text-white mb-2">Stream Not Found</h2>
          <p className="text-white/60 mb-4">This stream doesn't exist or has been removed.</p>
          <Button onClick={() => navigate('/discovery')}>
            Browse Live Streams
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title={stream ? `${stream.title} - Watch Live` : 'Watch Live Stream'}
        description={stream?.description || 'Watch this exciting live stream'}
        keywords={['live stream', 'watch live', 'streaming']}
      />
      
      {/* Full-screen immersive layout (TikTok-style) */}
      <div className="fixed inset-0 bg-black">
        {/* Video Player - Full Screen Background */}
        <div className="absolute inset-0">
          {streamManagement?.playback_url ? (
            <WebRTCPlayer
              streamId={id!}
              viewerId={currentUserId || 'anonymous'}
            />
          ) : stream?.thumbnail_url ? (
            <div className="w-full h-full flex items-center justify-center bg-black">
              <img src={stream.thumbnail_url} alt="Stream" className="w-full h-full object-cover opacity-50" />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-black">
              <p className="text-white">Stream loading...</p>
            </div>
          )}
        </div>
        
          {/* Overlay Controls */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Back Button - Top Left */}
            <div className="absolute top-4 left-4 pointer-events-auto">
              <BackButton to="/discovery" label="Back" />
            </div>

            {/* Viewer Count & Controls - Top Right */}
            <div className="absolute top-4 right-4 flex flex-col gap-2 pointer-events-auto">
              <div className="bg-black/60 backdrop-blur-md rounded-full px-4 py-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                <Users className="h-4 w-4 text-white" />
                <span className="text-sm font-bold text-white">{viewerCount}</span>
              </div>
              
              {/* PIP and Clip buttons */}
              <div className="flex gap-2">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-10 w-10 bg-black/60 backdrop-blur-md hover:bg-black/80"
                  onClick={togglePictureInPicture}
                  title="Picture in Picture"
                >
                  <PictureInPicture2 className={`h-5 w-5 ${isPiPActive ? 'text-primary' : 'text-white'}`} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-10 w-10 bg-black/60 backdrop-blur-md hover:bg-black/80"
                  onClick={() => setShowClipCreator(true)}
                  title="Create Clip"
                >
                  <Scissors className="h-5 w-5 text-white" />
                </Button>
              </div>
            </div>

          {/* Chat Overlay - Right Side (TikTok-style) */}
          <div className="absolute bottom-32 right-4 w-80 max-h-[50vh] pointer-events-auto">
            <div className="flex flex-col gap-3">
              {/* Scrollable Messages */}
              <div className="space-y-2 overflow-y-auto scrollbar-hide">
                {messages.slice(-8).map((msg) => (
                  <div
                    key={msg.id}
                    className="bg-black/50 backdrop-blur-md rounded-2xl px-4 py-2 text-sm animate-fade-in"
                  >
                    {msg.is_tip ? (
                      <div className="flex items-center gap-2">
                        <Coins className="h-4 w-4 text-yellow-400" />
                        <span className="font-bold text-yellow-400">{msg.sender_name}</span>
                        <span className="text-white">{msg.message}</span>
                      </div>
                    ) : (
                      <div>
                        <span className="font-semibold text-white">{msg.sender_name}: </span>
                        <span className="text-white/90">{msg.message}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Chat Input */}
              <div className="bg-black/60 backdrop-blur-md rounded-2xl px-4 py-3 flex items-center gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Say something..."
                  className="flex-1 bg-white/10 border-none text-white placeholder:text-white/50"
                />
                <Button size="icon" onClick={sendMessage} variant="ghost" className="text-white hover:text-white/80">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Action Buttons - Right Side (TikTok-style vertical stack) */}
          <div className="absolute bottom-4 right-4 flex flex-col gap-4 items-center pointer-events-auto">
            {/* Creator Avatar */}
            <Avatar className="h-12 w-12 border-2 border-white shadow-lg">
              <AvatarImage src={stream?.profiles?.avatar_url} />
              <AvatarFallback>{stream?.profiles?.display_name?.[0]}</AvatarFallback>
            </Avatar>

            {/* Tip Button */}
            <div className="flex flex-col items-center gap-1">
              <Button
                size="icon"
                onClick={() => {
                  const amount = prompt('Enter tip amount:');
                  if (amount) {
                    setTipAmount(amount);
                    setTimeout(sendTip, 100);
                  }
                }}
                className="h-14 w-14 rounded-full bg-gradient-to-br from-pink-500 to-red-500 shadow-2xl hover:scale-110 transition-transform border-2 border-white"
              >
                <Heart className="h-6 w-6 text-white" fill="currentColor" />
              </Button>
              <span className="text-xs text-white drop-shadow-lg font-bold">
                {stream.total_tips || 0}
              </span>
            </div>

            {/* Share Button */}
            <div className="flex flex-col items-center gap-1">
              <Button
                size="icon"
                className="h-12 w-12 rounded-full bg-black/60 backdrop-blur-md shadow-lg hover:scale-110 transition-transform"
                onClick={() => {
                  navigator.share?.({
                    title: stream?.title,
                    url: window.location.href,
                  }).catch(() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Link copied!');
                  });
                }}
              >
                <Share2 className="h-5 w-5 text-white" />
              </Button>
              <span className="text-xs text-white drop-shadow-lg font-semibold">Share</span>
            </div>

            {/* Wallet Balance */}
            <div className="flex flex-col items-center gap-1 mt-2">
              <div className="bg-black/60 backdrop-blur-md rounded-full px-3 py-2 flex items-center gap-1">
                <Coins className="h-4 w-4 text-yellow-400" />
                <span className="text-xs text-white font-bold">{wallet?.balance || 0}</span>
              </div>
            </div>
          </div>

          {/* Stream Info Overlay - Bottom Left */}
          <div className="absolute bottom-4 left-4 right-[26rem] pointer-events-auto">
            <div className="bg-black/50 backdrop-blur-md rounded-2xl px-6 py-4 max-w-lg">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="destructive" className="animate-pulse">
                    🔴 LIVE
                  </Badge>
                  <span className="text-xs text-white/60">
                    {new Date(stream.created_at).toLocaleTimeString()}
                  </span>
                </div>
                <h1 className="font-bold text-xl text-white line-clamp-2">{stream?.title}</h1>
                <p className="text-sm text-white/80">@{stream?.profiles?.display_name}</p>
                {stream?.description && (
                  <p className="text-sm text-white/70 line-clamp-2">{stream.description}</p>
                )}
              </div>
            </div>
          </div>

          {/* Active Poll Overlay - Bottom Center */}
          {polls.filter(p => p.is_active && (!p.ends_at || new Date(p.ends_at) >= new Date())).length > 0 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-80 pointer-events-auto">
              <div className="bg-black/60 backdrop-blur-md rounded-2xl p-4 space-y-3">
                <h3 className="font-bold text-sm flex items-center gap-2 text-white">
                  <Trophy className="h-4 w-4 text-yellow-400" />
                  Active Poll
                </h3>
                {polls.filter(p => p.is_active && (!p.ends_at || new Date(p.ends_at) >= new Date())).slice(0, 1).map((poll) => (
                  <div key={poll.id} className="space-y-2">
                    <p className="text-sm font-medium text-white">{poll.question}</p>
                    <div className="space-y-1">
                      {poll.options.map((option: string, idx: number) => (
                        <Button
                          key={idx}
                          onClick={() => voteOnPoll(poll.id, idx, poll.cost)}
                          variant="secondary"
                          size="sm"
                          className="w-full justify-start bg-white/20 hover:bg-white/30 text-white"
                        >
                          {option}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-white/60">{poll.cost} coins per vote</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tip Celebration Animation */}
        {tipCelebration && (
          <TipCelebration
            amount={tipCelebration.amount}
            userName={tipCelebration.userName}
            onComplete={() => setTipCelebration(null)}
          />
        )}

        {/* Live Reactions */}
        <LiveReactions />

        {/* Clip Creator Dialog */}
        <ClipCreator
          streamId={id!}
          open={showClipCreator}
          onOpenChange={setShowClipCreator}
        />
      </div>
    </>
  );
};

export default StreamPlayer;
